import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Grid,
} from "@material-ui/core";
import ColorBoxModal from "../ColorBoxModal";

import { X as CloseIcon, Pipette, Tag as TagIcon, Clock } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { AuthContext } from "../../context/Auth/AuthContext";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";
  return {
    dialogPaper: {
      borderRadius: "21px 21px 12px 12px !important",
      maxHeight: "85vh !important",
      display: "flex !important",
      flexDirection: "column !important",
      overflow: "hidden !important",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none !important",
      border: "none",
      "& form": {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      },
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
      "& .MuiOutlinedInput-root": { borderRadius: 12 },
      "& .MuiButton-root": { borderRadius: 12 },
      "& .MuiSelect-icon": { right: 8 },
    },
    dialogHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 16px",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#ffffff",
      borderBottom: "none",
      flexShrink: 0,
    },
    dialogHeaderTitle: {
      fontWeight: 600,
      fontSize: 15,
      color: "inherit",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    },
    closeBtn: {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      color: "#ffffff",
      transition: "all 0.2s ease",
      padding: 6,
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.22)",
        color: "#ffffff",
      },
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: isDark ? "#8696a0" : "#64748b",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.5),
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    colorAdorment: {
      width: 24,
      height: 24,
      borderRadius: 8,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.15)"}`,
      flexShrink: 0,
    },
    colorPickerOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2,
    },
    colorPickerWrap: {
      position: "absolute",
      zIndex: 3,
      marginTop: 4,
      borderRadius: 12,
      overflow: "hidden",
      boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.55)" : "0 12px 40px rgba(15,23,42,0.18)",
    },
    presetSwatch: {
      width: 28,
      height: 28,
      borderRadius: 8,
      cursor: "pointer",
      border: `2px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
      transition: "transform 0.15s ease, border-color 0.15s ease",
      "&:hover": {
        transform: "scale(1.08)",
      },
    },
    presetSwatchActive: {
      borderColor: theme.palette.primary.main,
      transform: "scale(1.08)",
    },
    presetRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 6,
    },
    timeInputsLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: isDark ? "#8696a0" : "#64748b",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 4,
    },
  };
});

const PRESET_COLORS = [
  "#2EA85A", "#3b82f6", "#8b5cf6", "#F7C82C", "#e53935", "#06b6d4",
  "#f97316", "#ec4899", "#14b8a6", "#a855f7", "#0ea5e9", "#84cc16",
];

const TagSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, i18n.t("tagModal.validation.tooShort"))
    .required(i18n.t("tagModal.validation.required")),
});

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

function getRandomHexColor() {
  return PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)];
}

const TagModal = ({ open, onClose, tagId, kanban, onSaveSuccess }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();
  const { user } = useContext(AuthContext);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [lanes, setLanes] = useState([]);
  const [, setLoading] = useState(false);
  const [selectedLane, setSelectedLane] = useState([]);
  const [selectedRollbackLane, setSelectedRollbackLane] = useState([]);
  const [timeDays, setTimeDays] = useState(0);
  const [timeHours, setTimeHours] = useState(0);
  const [timeMinutes, setTimeMinutes] = useState(0);
  const [timeSeconds, setTimeSeconds] = useState(0);

  const initialState = {
    name: "",
    color: getRandomHexColor(),
    kanban,
    timeLane: 0,
    nextLaneId: 0,
    greetingMessageLane: "",
    rollbackLaneId: 0,
  };

  const [tag, setTag] = useState(initialState);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", { params: { kanban: 1, tagId } });
          setLanes(data.tags);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      (async () => {
        if (!tagId) return;
        const { data } = await api.get(`/tags/${tagId}`);
        setTag((prevState) => ({ ...prevState, ...data }));
        if (data.nextLaneId) setSelectedLane(data.nextLaneId);
        if (data.rollbackLaneId) setSelectedRollbackLane(data.rollbackLaneId);
        if (data.timeLane) {
          const decomposed = decomposeHours(data.timeLane);
          setTimeDays(decomposed.days);
          setTimeHours(decomposed.hours);
          setTimeMinutes(decomposed.minutes);
          setTimeSeconds(decomposed.seconds);
        }
      })();
    } catch (err) {
      toastError(err);
    }
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorPickerModalOpen(false);
    setTimeDays(0);
    setTimeHours(0);
    setTimeMinutes(0);
    setTimeSeconds(0);
    onClose();
  };

  const handleSaveTag = async (values) => {
    const totalTimeLane = calculateTotalHours(timeDays, timeHours, timeMinutes, timeSeconds);
    const tagData = {
      ...values,
      userId: user?.id,
      kanban,
      nextLaneId: selectedLane || null,
      rollbackLaneId: selectedRollbackLane || null,
      timeLane: totalTimeLane,
    };

    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post("/tags", tagData);
      }
      toast.success(kanban === 0 ? i18n.t("tagModal.success") : i18n.t("tagModal.successKanban"));
      if (onSaveSuccess) {
        setTimeout(() => onSaveSuccess(), 500);
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const calculateTotalHours = (days, hours, minutes, seconds) =>
    (parseInt(days) || 0) * 24 +
    (parseInt(hours) || 0) +
    (parseInt(minutes) || 0) / 60 +
    (parseInt(seconds) || 0) / 3600;

  const decomposeHours = (totalHours) => {
    const hours = parseFloat(totalHours) || 0;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    const fractionalHour = hours % 1;
    const totalMinutes = fractionalHour * 60;
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.round((totalMinutes % 1) * 60);
    return { days, hours: remainingHours, minutes, seconds };
  };

  const dialogTitle = tagId
    ? kanban === 0
      ? i18n.t("tagModal.title.edit")
      : i18n.t("tagModal.title.editKanban")
    : kanban === 0
    ? i18n.t("tagModal.title.add")
    : i18n.t("tagModal.title.addKanban");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={kanban === 1 ? "md" : "sm"}
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>
          <TagIcon size={18} strokeWidth={2} />
          {dialogTitle}
        </Typography>
        <IconButton size="small" onClick={handleClose} className={classes.closeBtn}>
          <CloseIcon size={16} />
        </IconButton>
      </div>

      <Formik
        initialValues={tag}
        enableReinitialize={true}
        validationSchema={TagSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveTag(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, values }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label={i18n.t("tagModal.form.name")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    size="small"
                    onChange={(e) => setTag((prev) => ({ ...prev, name: e.target.value }))}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={i18n.t("tagModal.form.color")}
                    name="color"
                    id="color"
                    error={touched.color && Boolean(errors.color)}
                    helperText={touched.color && errors.color}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <div style={{ backgroundColor: values.color }} className={classes.colorAdorment} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <IconButton
                          size="small"
                          onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
                          style={{ borderRadius: 8 }}
                        >
                          <Pipette size={16} strokeWidth={2} />
                        </IconButton>
                      ),
                    }}
                  />
                  <div className={classes.presetRow}>
                    {PRESET_COLORS.map((c) => (
                      <div
                        key={c}
                        className={`${classes.presetSwatch} ${values.color?.toLowerCase() === c.toLowerCase() ? classes.presetSwatchActive : ""}`}
                        style={{ background: c }}
                        onClick={() => setTag((prev) => ({ ...prev, color: c }))}
                        title={c}
                      />
                    ))}
                  </div>
                  <ColorBoxModal
                    open={colorPickerModalOpen}
                    handleClose={() => setColorPickerModalOpen(false)}
                    currentColor={tag.color}
                    onChange={(hex) => setTag((prev) => ({ ...prev, color: hex }))}
                  />
                </Grid>

                {kanban === 1 && (
                  <>
                    <Grid item xs={12}>
                      <Typography className={classes.sectionLabel}>
                        <Clock size={12} strokeWidth={2.5} />
                        {tLang("tagModal.form.timeOnLane", "Tempo na lane")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label={tLang("tagModal.form.days", "Dias")}
                        type="number"
                        value={timeDays}
                        variant="outlined"
                        size="small"
                        onChange={(e) => setTimeDays(e.target.value)}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label={tLang("tagModal.form.hours", "Horas")}
                        type="number"
                        value={timeHours}
                        variant="outlined"
                        size="small"
                        onChange={(e) => setTimeHours(e.target.value)}
                        fullWidth
                        inputProps={{ min: 0, max: 23 }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label={tLang("tagModal.form.minutes", "Minutos")}
                        type="number"
                        value={timeMinutes}
                        variant="outlined"
                        size="small"
                        onChange={(e) => setTimeMinutes(e.target.value)}
                        fullWidth
                        inputProps={{ min: 0, max: 59 }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label={tLang("tagModal.form.seconds", "Segundos")}
                        type="number"
                        value={timeSeconds}
                        variant="outlined"
                        size="small"
                        onChange={(e) => setTimeSeconds(e.target.value)}
                        fullWidth
                        inputProps={{ min: 0, max: 59 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel id="nextLane-label">{i18n.t("tagModal.form.nextLaneId")}</InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("tagModal.form.nextLaneId")}
                          labelId="nextLane-label"
                          id="nextLaneId"
                          name="nextLaneId"
                          error={touched.nextLaneId && Boolean(errors.nextLaneId)}
                          value={selectedLane}
                          onChange={(e) => setSelectedLane(e.target.value || null)}
                          MenuProps={{ classes: { paper: dropdownClasses.paper } }}
                        >
                          <MenuItem value={null}>&nbsp;</MenuItem>
                          {lanes &&
                            lanes.map((lane) => (
                              <MenuItem key={lane.id} value={lane.id}>
                                {lane.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Field
                        as={TextField}
                        label={i18n.t("tagModal.form.greetingMessageLane")}
                        name="greetingMessageLane"
                        minRows={4}
                        multiline
                        error={touched.greetingMessageLane && Boolean(errors.greetingMessageLane)}
                        helperText={touched.greetingMessageLane && errors.greetingMessageLane}
                        variant="outlined"
                        size="small"
                        onChange={(e) => setTag((prev) => ({ ...prev, greetingMessageLane: e.target.value }))}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel id="rollback-label">{i18n.t("tagModal.form.rollbackLaneId")}</InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("tagModal.form.rollbackLaneId")}
                          labelId="rollback-label"
                          id="rollbackLaneId"
                          name="rollbackLaneId"
                          error={touched.rollbackLaneId && Boolean(errors.rollbackLaneId)}
                          value={selectedRollbackLane}
                          onChange={(e) => setSelectedRollbackLane(e.target.value)}
                          MenuProps={{ classes: { paper: dropdownClasses.paper } }}
                        >
                          <MenuItem value={null}>&nbsp;</MenuItem>
                          {lanes &&
                            lanes.map((lane) => (
                              <MenuItem key={lane.id} value={lane.id}>
                                {lane.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <ChatiaDialogActions
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              saveText={tagId ? i18n.t("tagModal.buttons.okEdit") : i18n.t("tagModal.buttons.okAdd")}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TagModal;
